import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { ValidateMetadataRequest } from '../../Model/requests/ValidateMetadataRequest';
import { useLoaderContext } from '../loader/GearLoaderContext';
import { LOADER_KEY } from '../../Model/enums/GearLoaderKey';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useValidateMetadata(): UseMutationResult<string[], unknown, { dto: ValidateMetadataRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const { showLoader } = useLoaderContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showToast } = useNotificationContext();

  const validateDocumentMetadata = (data: { dto: ValidateMetadataRequest }): Promise<string[]> => {
    if (servicesCtx?.documentsService && data.dto) {
      showLoader(true, LOADER_KEY.VALIDATE_DOCUMENT_METADATA);
      return servicesCtx.documentsService?.validateDocumentMetadata(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.validateDocumentMetadata,
    mutationFn: (data: { dto: ValidateMetadataRequest }) => validateDocumentMetadata(data),
    onSuccess: errors => {
      if (errors.length > 0) {
        showToast({
          type: GEAR_NOTIFICATION_TYPE.ERROR,
          message: errors.join(', ')
        });
      }
    },
    onError: () => {
      showToast({
        type: GEAR_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.toolbar.actions.form.metadataValidationFailed')
      });
    },
    onSettled: () => {
      showLoader(false, LOADER_KEY.VALIDATE_DOCUMENT_METADATA);
    }
  });
}
